import { ChainAddress, EthereumAddress, SolanaAddress } from '@/utils/models';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';

import { CHAIN_IDS } from './chain';

// NOTE: All the EVM contract addresses should be lowercased.

export const NULL_ADDRESS = '0x0000000000000000000000000000000000000000';
export const NULL_STAKING_POOL =
  '0x0000000000000000000000000000000000000000000000000000000000000000';

export const BURN_ADDRESS_EVM = '0x000000000000000000000000000000000000dead' as EthereumAddress;
export const BURN_ADDRESS_SOLANA = 'dead111111111111111111111111111111111111111' as SolanaAddress;

export const PLACEHOLDER_ADDRESS_EVM =
  '0x56EB0aD2dC746540Fab5C02478B31e2AA9DdC38C' as EthereumAddress;
export const PLACEHOLDER_ADDRESS_SOLANA =
  '2xQoML4snWGKvgAgCZMbnvk4H1UhiybpyKYCQtNjWTVU' as SolanaAddress;

// zeroex-dev.sol: placeholder taker used to quote a Solana swap when no wallet
// is connected. It holds a little SOL and no token accounts, so a simulation
// against it reverts on almost any trade.
export const FAKE_TAKER_ADDRESS_SOLANA =
  '3zSiMfexWoWY8Yjvpd2bofNrUiCfH2S5Q9a7BwqiGUqM' as SolanaAddress;

export const ETH_FAKE_ADDRESS = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' as const;

interface ContractByChainId {
  [chainId: number]: ChainAddress;
}

// WETH
const WETH_ADDRESS_MAINNET = '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2';
const WETH_ADDRESS_SCROLL = '0x5300000000000000000000000000000000000004';
const WETH_ADDRESS_ARBITRUM = '0x82af49447d8a07e3bd95bd0d56f35241523fbab1';
const WETH_ADDRESS_MATIC = '0x7ceb23fd6bc0add59e62ac25578270cff1b9f619';
const WETH_ADDRESS_OPTIMISM = '0x4200000000000000000000000000000000000006';
const WETH_ADDRESS_BASE = '0x4200000000000000000000000000000000000006';
const WETH_ADDRESS_LINEA = '0xe5d7c2a44ffddf6b295a15c148167daaaf5cf34f';
const WETH_ADDRESS_MANTLE = '0xdeaddeaddeaddeaddeaddeaddeaddeaddead1111';
const WETH_ADDRESS_UNICHAIN = '0x4200000000000000000000000000000000000006';
const WETH_ADDRESS_PLASMA = '0x9895d81bb462a195b4922ed7de0e3acd007c32cb';
const WETH_ADDRESS_MONAD = '0xee8c0e9f1bffb4eb878d8f15f368a02a35481242';
const WETH_ADDRESS_ABSTRACT = '0x3439153eb7af838ad19d56e1571fbd09333c2809';
const WETH_ADDRESS_ROBINHOOD = '0x0bd7d308f8e1639fab988df18a8011f41eacad73';
const WETH_ADDRESS_INK = '0x4200000000000000000000000000000000000006';
// Wrapped tokens (not WETH)
const WAVAX_ADDRESS_AVALANCHE = '0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7';
const BNB_ADDRESS_BSC = '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c';
const WMATIC_ADDRESS_MATIC = '0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270';
const WMNT_ADDRESS_MANTLE = '0x78c1b0c915c4faa5fffa6cabf0219da63d7f4cb8';
const WXPL_ADDRESS_PLASMA = '0x6100e367285b01f48d07953803a2d8dca5d19873';
const WHYPE_ADDRESS_HYPEREVM = '0x5555555555555555555555555555555555555555';
/** The wSOL SPL mint. A distinct, separately tradeable token from native SOL (like WETH vs ETH). */
export const WSOL_ADDRESS_SOLANA = 'So11111111111111111111111111111111111111112' as SolanaAddress;
const WMON_ADDRESS_MONAD = '0x3bd359c1119da7da1d913d1c4d2b7c461115433a';
// Other tokens
const MATIC_ADDRESS_MATIC = '0x0000000000000000000000000000000000001010';
export const POL_ADDRESS_MAINNET = '0x455e53cbb86018ac2b8092fdcd39d8444affc3f6';
const MNT_ADDRESS_MANTLE = '0xdeaddeaddeaddeaddeaddeaddeaddeaddead0000';
export const ZORA_TOKEN_ADDRESS_BASE = '0x1111111111166b7fe7bd91427724b487980afc69';

/**
 * Native SOL. Not an on-chain account: it is the sentinel the 0x APIs use for
 * native SOL (the Solana analogue of `0xeeee…`) and Matcha's identity for the
 * user's lamport balance. Map it to `WSOL_ADDRESS_SOLANA` before hitting any
 * mint-keyed integration (Jupiter, Definitive, prices, image proxy).
 */
export const SOL_ADDRESS_SOLANA = 'So11111111111111111111111111111111111111111' as SolanaAddress;

export const NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID: ContractByChainId = {
  [CHAIN_IDS.MAINNET]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.ARBITRUM]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.AVALANCHE]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.BASE]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.MATIC]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.OPTIMISM]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.SCROLL]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.BSC]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.LINEA]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.MANTLE]: MNT_ADDRESS_MANTLE,
  [CHAIN_IDS.SOLANA]: SOL_ADDRESS_SOLANA,
  [CHAIN_IDS.UNICHAIN]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.PLASMA]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.MONAD]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.ABSTRACT]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.HYPEREVM]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.ROBINHOOD]: ETH_FAKE_ADDRESS,
  [CHAIN_IDS.INK]: ETH_FAKE_ADDRESS,
} as const;

export const WETH_ADDRESSES: ContractByChainId = {
  [CHAIN_IDS.MAINNET]: WETH_ADDRESS_MAINNET,
  [CHAIN_IDS.ARBITRUM]: WETH_ADDRESS_ARBITRUM,
  [CHAIN_IDS.BASE]: WETH_ADDRESS_BASE,
  [CHAIN_IDS.MATIC]: WETH_ADDRESS_MATIC,
  [CHAIN_IDS.OPTIMISM]: WETH_ADDRESS_OPTIMISM,
  [CHAIN_IDS.SCROLL]: WETH_ADDRESS_SCROLL,
  [CHAIN_IDS.LINEA]: WETH_ADDRESS_LINEA,
  [CHAIN_IDS.MANTLE]: WETH_ADDRESS_MANTLE,
  [CHAIN_IDS.UNICHAIN]: WETH_ADDRESS_UNICHAIN,
  [CHAIN_IDS.PLASMA]: WETH_ADDRESS_PLASMA,
  [CHAIN_IDS.ABSTRACT]: WETH_ADDRESS_ABSTRACT,
  [CHAIN_IDS.ROBINHOOD]: WETH_ADDRESS_ROBINHOOD,
  [CHAIN_IDS.INK]: WETH_ADDRESS_INK,
} as const;

export const WRAPPED_NATIVE_ADDRESSES: ContractByChainId = {
  ...WETH_ADDRESSES,
  [CHAIN_IDS.AVALANCHE]: WAVAX_ADDRESS_AVALANCHE,
  [CHAIN_IDS.BSC]: BNB_ADDRESS_BSC,
  [CHAIN_IDS.MATIC]: WMATIC_ADDRESS_MATIC, // WMATIC replaces MATIC WETH
  [CHAIN_IDS.MANTLE]: WMNT_ADDRESS_MANTLE,
  [CHAIN_IDS.PLASMA]: WXPL_ADDRESS_PLASMA,
  [CHAIN_IDS.SOLANA]: WSOL_ADDRESS_SOLANA,
  [CHAIN_IDS.MONAD]: WMON_ADDRESS_MONAD,
  [CHAIN_IDS.HYPEREVM]: WHYPE_ADDRESS_HYPEREVM,
} as const;

interface NativeTokenAddressForBlockExplorerByChainId {
  [chainId: number]: ChainAddress | undefined;
}
export const NATIVE_TOKEN_ADDRESS_FOR_BLOCK_EXPLORER_BY_CHAIN_ID: NativeTokenAddressForBlockExplorerByChainId =
  {
    [CHAIN_IDS.MATIC]: MATIC_ADDRESS_MATIC,
    [CHAIN_IDS.MANTLE]: MNT_ADDRESS_MANTLE,
  } as const;

// USDC
const USDC_ADDRESS_MAINNET = '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48';
const USDC_ADDRESS_ARBITRUM = '0xaf88d065e77c8cc2239327c5edb3a432268e5831';
const USDC_ADDRESS_MATIC = '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359';
const USDC_ADDRESS_OPTIMISM = '0x0b2c639c533813f4aa9d7837caf62653d097ff85';
const USDC_ADDRESS_BASE = '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913';
const USDC_ADDRESS_BSC = '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d';
const USDC_ADDRESS_AVALANCHE = '0xb97ef9ef8734c71904d8002f8b6bc66dd9c48a6e';
const USDC_ADDRESS_SCROLL = '0x06efdbff2a14a7c8e15944d1f4a48f9f95f663a4';
const USDC_ADDRESS_LINEA = '0x176211869ca2b568f2a7d4ee941e073a821ee1ff';
const USDC_ADDRESS_MANTLE = '0x09bc4e0d864854c6afb6eb9a9cdf58ac190d0df9';
const USDC_ADDRESS_UNICHAIN = '0x078d782b760474a361dda0af3839290b0ef57ad6';
const USDC_ADDRESS_SOLANA = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v' as SolanaAddress;
const USDC_ADDRESS_MONAD = '0x754704bc059f8c67012fed69bc8a327a5aafb603';
const USDC_ADDRESS_ABSTRACT = '0x84a71ccd554cc1b02749b35d22f684cc8ec987e1';
const USDC_ADDRESS_HYPEREVM = '0xb88339cb7199b77e23db6e890353e22632ba630f';
const USDC_ADDRESS_INK = '0x2d270e6886d130d724215a266106e6832161eaed';

// USDT
const USDT_ADDRESS_MAINNET = '0xdac17f958d2ee523a2206206994597c13d831ec7';
const USDT_ADDRESS_ARBITRUM = '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9';
const USDT_ADDRESS_MATIC = '0xc2132d05d31c914a87c6611c10748aeb04b58e8f';
const USDT_ADDRESS_OPTIMISM = '0x94b008aa00579c1307b0ef2c499ad98a8ce58e58';
const USDT_ADDRESS_BSC = '0x55d398326f99059ff775485246999027b3197955';
const USDT_ADDRESS_AVALANCHE = '0x9702230a8ea53601f5cd2dc00fdbc13d4df4a8c7';
const USDT_ADDRESS_SCROLL = '0xf55bec9cafdbe8730f096aa55dad6d22d44099df';
const USDT_ADDRESS_LINEA = '0xa219439258ca9da29e9cc4ce5596924745e12b93';
const USDT_ADDRESS_MANTLE = '0x201eba5cc46d216ce6dc03f6a759e8e766e956ae';
const USDT_ADDRESS_UNICHAIN = '0x9151434b16b9763660705744891fa906f660ecc5';
const USDT_ADDRESS_SOLANA = 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB' as SolanaAddress;
const USDT_ADDRESS_ABSTRACT = '0x0709f39376deee2a2dfc94a58edeb2eb9df012bd';

// DAI
const DAI_ADDRESS_MAINNET = '0x6b175474e89094c44da98b954eedeac495271d0f';
const DAI_ADDRESS_ARBITRUM = '0xda10009cbd5d07dd0cecc66161fc93d7c9000da1';
const DAI_ADDRESS_MATIC = '0x8f3cf7ad23cd3cadbd9735aff958023239c6a063';
const DAI_ADDRESS_OPTIMISM = '0xda10009cbd5d07dd0cecc66161fc93d7c9000da1';
const DAI_ADDRESS_BASE = '0x50c5725949a6f0c72e6c4a641f24049a917db0cb';
const DAI_ADDRESS_BSC = '0x1af3f329e8be154074d8769d1ffa4ee058b1dbc3';
const DAI_ADDRESS_AVALANCHE = '0xd586e7f844cea2f87f50152665bcbc2c279d8d70';
const DAI_ADDRESS_SCROLL = '0xca77eb3fefe3725dc33bccb54edefc3d9f764f97';
const DAI_ADDRESS_LINEA = '0x4af15ec2a0bd43db75dd04e62faa3b8ef36b00d5';
const DAI_ADDRESS_UNICHAIN = '0x20cab320a855b39f724131c69424240519573f81';

// WBTC
const WBTC_ADDRESS_MAINNET = '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599';
const WBTC_ADDRESS_ARBITRUM = '0x2f2a2543b76a4166549f7aab2e75bef0aefc5b0f';
const WBTC_ADDRESS_MATIC = '0x1bfd67037b42cf73acf2047067bd4f2c47d9bfd6';
const WBTC_ADDRESS_OPTIMISM = '0x68f180fcce6836688e9084f035309e29bf0a2095';
const WBTC_ADDRESS_AVALANCHE = '0x50b7545627a5162f82a992c33b87adc75187b218';
const WBTC_ADDRESS_SCROLL = '0x3c1bca5a656e69edcd0d4e36bebb3fcdaca60cf1';
const WBTC_ADDRESS_LINEA = '0x3aab2285ddcddad8edf438c1bab47e1a9d05a9b4';
const WBTC_ADDRESS_MANTLE = '0xcabae6f6ea1ecab08ad02fe02ce9a44f09aebfa2';
const WBTC_ADDRESS_UNICHAIN = '0x927b51f251480a681271180da4de28d44ec4afb8';
export const WBTC_ADDRESS_SOLANA = '3NZ9JMVBmGAqocybic2c7LQCJScmgsAZ6vQqTDzcqmJh' as SolanaAddress;

// Additional Token Suggestions
const USDbC_ADDRESS_BASE = '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca';
const USDT0_ADDRESS_PLASMA = '0xb8ce59fc3717ada4c02eadf9682a9e934f625ebb';
// USDT0 on HyperEVM is deployed at the same deterministic address as Plasma's USDT0.
const USDT0_ADDRESS_HYPEREVM = '0xb8ce59fc3717ada4c02eadf9682a9e934f625ebb';
export const AUSD_ADDRESS_MONAD = '0x00000000efe302beaa2b3e6e1b18d08d69a9012a';
export const USDT0_ADDRESS_MONAD = '0xe7cd86e13ac4309349f30b3435a9d337750fc82d';
const USDG_ADDRESS_ROBINHOOD = '0x5fc5360d0400a0fd4f2af552add042d716f1d168';
// USDT0 on Ink (symbol USD₮0), 6 decimals.
const USDT0_ADDRESS_INK = '0x0200c29006150606b650577bbe7b6248f58470c1';

export const USDC_ADDRESSES: ContractByChainId = {
  [CHAIN_IDS.MAINNET]: USDC_ADDRESS_MAINNET,
  [CHAIN_IDS.ARBITRUM]: USDC_ADDRESS_ARBITRUM,
  [CHAIN_IDS.MATIC]: USDC_ADDRESS_MATIC,
  [CHAIN_IDS.OPTIMISM]: USDC_ADDRESS_OPTIMISM,
  [CHAIN_IDS.BSC]: USDC_ADDRESS_BSC,
  [CHAIN_IDS.AVALANCHE]: USDC_ADDRESS_AVALANCHE,
  [CHAIN_IDS.BASE]: USDC_ADDRESS_BASE,
  [CHAIN_IDS.SCROLL]: USDC_ADDRESS_SCROLL,
  [CHAIN_IDS.LINEA]: USDC_ADDRESS_LINEA,
  [CHAIN_IDS.MANTLE]: USDC_ADDRESS_MANTLE,
  [CHAIN_IDS.UNICHAIN]: USDC_ADDRESS_UNICHAIN,
  [CHAIN_IDS.SOLANA]: USDC_ADDRESS_SOLANA,
  [CHAIN_IDS.MONAD]: USDC_ADDRESS_MONAD,
  [CHAIN_IDS.ABSTRACT]: USDC_ADDRESS_ABSTRACT,
  [CHAIN_IDS.HYPEREVM]: USDC_ADDRESS_HYPEREVM,
  [CHAIN_IDS.INK]: USDC_ADDRESS_INK,
} as const;

export const NATIVE_USDC_PER_CHAIN_ID: ContractByChainId = {
  [CHAIN_IDS.AVALANCHE]: USDC_ADDRESS_AVALANCHE,
  [CHAIN_IDS.ARBITRUM]: USDC_ADDRESS_ARBITRUM,
  [CHAIN_IDS.BASE]: USDC_ADDRESS_BASE,
  [CHAIN_IDS.MAINNET]: USDC_ADDRESS_MAINNET,
  [CHAIN_IDS.MATIC]: USDC_ADDRESS_MATIC,
  [CHAIN_IDS.OPTIMISM]: USDC_ADDRESS_OPTIMISM,
  [CHAIN_IDS.UNICHAIN]: USDC_ADDRESS_UNICHAIN,
  [CHAIN_IDS.SOLANA]: USDC_ADDRESS_SOLANA,
  [CHAIN_IDS.LINEA]: USDC_ADDRESS_LINEA,
  [CHAIN_IDS.MONAD]: USDC_ADDRESS_MONAD,
  [CHAIN_IDS.HYPEREVM]: USDC_ADDRESS_HYPEREVM,
  [CHAIN_IDS.INK]: USDC_ADDRESS_INK,
};

export const USDT_ADDRESSES: ContractByChainId = {
  [CHAIN_IDS.MAINNET]: USDT_ADDRESS_MAINNET,
  [CHAIN_IDS.MATIC]: USDT_ADDRESS_MATIC,
  [CHAIN_IDS.BSC]: USDT_ADDRESS_BSC,
  [CHAIN_IDS.AVALANCHE]: USDT_ADDRESS_AVALANCHE,
  [CHAIN_IDS.OPTIMISM]: USDT_ADDRESS_OPTIMISM,
  [CHAIN_IDS.ARBITRUM]: USDT_ADDRESS_ARBITRUM,
  [CHAIN_IDS.SCROLL]: USDT_ADDRESS_SCROLL,
  [CHAIN_IDS.LINEA]: USDT_ADDRESS_LINEA,
  [CHAIN_IDS.MANTLE]: USDT_ADDRESS_MANTLE,
  [CHAIN_IDS.UNICHAIN]: USDT_ADDRESS_UNICHAIN,
  [CHAIN_IDS.SOLANA]: USDT_ADDRESS_SOLANA,
  [CHAIN_IDS.ABSTRACT]: USDT_ADDRESS_ABSTRACT,
} as const;

// DAI
export const DAI_ADDRESSES: ContractByChainId = {
  [CHAIN_IDS.MAINNET]: DAI_ADDRESS_MAINNET,
  [CHAIN_IDS.ARBITRUM]: DAI_ADDRESS_ARBITRUM,
  [CHAIN_IDS.MATIC]: DAI_ADDRESS_MATIC,
  [CHAIN_IDS.OPTIMISM]: DAI_ADDRESS_OPTIMISM,
  [CHAIN_IDS.BASE]: DAI_ADDRESS_BASE,
  [CHAIN_IDS.BSC]: DAI_ADDRESS_BSC,
  [CHAIN_IDS.AVALANCHE]: DAI_ADDRESS_AVALANCHE,
  [CHAIN_IDS.SCROLL]: DAI_ADDRESS_SCROLL,
  [CHAIN_IDS.LINEA]: DAI_ADDRESS_LINEA,
  [CHAIN_IDS.UNICHAIN]: DAI_ADDRESS_UNICHAIN,
} as const;

export const WBTC_ADDRESSES: ContractByChainId = {
  [CHAIN_IDS.MAINNET]: WBTC_ADDRESS_MAINNET,
  [CHAIN_IDS.ARBITRUM]: WBTC_ADDRESS_ARBITRUM,
  [CHAIN_IDS.MATIC]: WBTC_ADDRESS_MATIC,
  [CHAIN_IDS.OPTIMISM]: WBTC_ADDRESS_OPTIMISM,
  [CHAIN_IDS.AVALANCHE]: WBTC_ADDRESS_AVALANCHE,
  [CHAIN_IDS.SCROLL]: WBTC_ADDRESS_SCROLL,
  [CHAIN_IDS.LINEA]: WBTC_ADDRESS_LINEA,
  [CHAIN_IDS.MANTLE]: WBTC_ADDRESS_MANTLE,
  [CHAIN_IDS.UNICHAIN]: WBTC_ADDRESS_UNICHAIN,
  [CHAIN_IDS.SOLANA]: WBTC_ADDRESS_SOLANA,
} as const;

export const ADDITIONAL_SUGGESTED_ADDRESSES: Record<number, ChainAddress[]> = {
  [CHAIN_IDS.BASE]: [USDbC_ADDRESS_BASE],
  [CHAIN_IDS.PLASMA]: [USDT0_ADDRESS_PLASMA],
  [CHAIN_IDS.MONAD]: [AUSD_ADDRESS_MONAD, WETH_ADDRESS_MONAD, USDT0_ADDRESS_MONAD],
  [CHAIN_IDS.HYPEREVM]: [USDT0_ADDRESS_HYPEREVM],
  [CHAIN_IDS.ROBINHOOD]: [USDG_ADDRESS_ROBINHOOD],
  [CHAIN_IDS.INK]: [USDT0_ADDRESS_INK],
};

// Most traded tokens by volume
// Ethereum https://etherscan.io/tokens?sort=24h_volume_usd&order=desc
// Polygon https://polygonscan.com/tokens?sort=24h_volume_usd&order=desc
// Optimism https://optimistic.etherscan.io/tokens?sort=24h_volume_usd&order=desc
// Base https://basescan.org/tokens?sort=24h_volume_usd&order=desc
// Arbitrum https://arbiscan.io/tokens?sort=24h_volume_usd&order=desc
// BSC https://bscscan.com/tokens?sort=24h_volume_usd&order=desc
// Avalanche https://snowscan.xyz/tokens?sort=24h_volume_usd&order=desc
export const MAIN_USD_STABLE_ADDRESSES: ContractByChainId = {
  ...USDT_ADDRESSES,
  ...USDC_ADDRESSES,
  [CHAIN_IDS.PLASMA]: USDT0_ADDRESS_PLASMA,
  [CHAIN_IDS.ROBINHOOD]: USDG_ADDRESS_ROBINHOOD,
} as const;

export const EXCHANGE_PROXY_ADDRESS = '0xdef1c0ded9bec7f1a1670819833240f027b25eff';

/**
 * Stablecoins on Solana
 * https://defillama.com/stablecoins/Solana
 */
export const FDUSD_ADDRESS_SOLANA = '9zNQRsGLjNKwCUU5Gq5LR8beUCPzQMVMqKAi3SSZh54u' as SolanaAddress;
export const PYUSD_ADDRESS_SOLANA = '2b1kV6DkPAnxd5ixfnxCpjxmKwqjjaYmCZfHsFu24GXo' as SolanaAddress;
export const USDY_ADDRESS_SOLANA = 'A1KLoBrKBde8Ty9qtNQUtq3C2ortoC3u7twggz7sEto6' as SolanaAddress;

export const SOLANA_STABLECOINS = [
  {
    symbol: 'USDC',
    address: USDC_ADDRESS_SOLANA,
  },
  {
    symbol: 'USDT',
    address: USDT_ADDRESS_SOLANA,
  },
  {
    symbol: 'FDUSD',
    address: FDUSD_ADDRESS_SOLANA,
  },
  {
    symbol: 'PYUSD',
    address: PYUSD_ADDRESS_SOLANA,
  },
  {
    symbol: 'USDY',
    address: USDY_ADDRESS_SOLANA,
  },
];

export const SOLANA_TOKEN_PROGRAM_ADDRESS = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
export const SOLANA_TOKEN_2022_PROGRAM_ADDRESS = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb';

/**
 * Major fee mints whose token program is pinned here, so the fee decision
 * needs no mint lookup. Fee-account existence is checked at runtime for every
 * mint, majors included, and the swap transaction creates a missing account
 * in-transaction on the first fill.
 */
export const SOLANA_SWAP_FEE_MINTS = {
  [WSOL_ADDRESS_SOLANA]: { tokenProgram: SOLANA_TOKEN_PROGRAM_ADDRESS },
  [USDC_ADDRESS_SOLANA]: { tokenProgram: SOLANA_TOKEN_PROGRAM_ADDRESS },
  [USDT_ADDRESS_SOLANA]: { tokenProgram: SOLANA_TOKEN_PROGRAM_ADDRESS },
  [FDUSD_ADDRESS_SOLANA]: {
    tokenProgram: SOLANA_TOKEN_PROGRAM_ADDRESS,
  },
  [PYUSD_ADDRESS_SOLANA]: {
    tokenProgram: SOLANA_TOKEN_2022_PROGRAM_ADDRESS,
  },
  [USDY_ADDRESS_SOLANA]: {
    tokenProgram: SOLANA_TOKEN_PROGRAM_ADDRESS,
  },
} as const;

/**
 * USD-pegged stablecoin addresses, grouped by chain ID. Used by clients that
 * need to ask "is this token a USD stablecoin on this chain?"
 *
 * Sourced from the per-token maps above (`USDC_ADDRESSES`, `USDT_ADDRESSES`,
 * `DAI_ADDRESSES`) plus chain-specific extras and `SOLANA_STABLECOINS`.
 */
export const USD_STABLE_ADDRESSES_BY_CHAIN_ID: Record<number, Set<string>> = (() => {
  const map: Record<number, Set<string>> = {};
  const add = (chainId: number, ...addresses: string[]) => {
    const set = (map[chainId] ??= new Set<string>());
    for (const addr of addresses) set.add(normalizeTokenAddress(addr, chainId));
  };
  for (const [chainId, address] of Object.entries(USDC_ADDRESSES)) add(Number(chainId), address);
  for (const [chainId, address] of Object.entries(USDT_ADDRESSES)) add(Number(chainId), address);
  for (const [chainId, address] of Object.entries(DAI_ADDRESSES)) add(Number(chainId), address);
  add(CHAIN_IDS.BASE, USDbC_ADDRESS_BASE);
  add(CHAIN_IDS.PLASMA, USDT0_ADDRESS_PLASMA);
  add(CHAIN_IDS.HYPEREVM, USDT0_ADDRESS_HYPEREVM);
  add(CHAIN_IDS.MONAD, AUSD_ADDRESS_MONAD, USDT0_ADDRESS_MONAD);
  add(CHAIN_IDS.ROBINHOOD, USDG_ADDRESS_ROBINHOOD);
  add(CHAIN_IDS.INK, USDT0_ADDRESS_INK);
  for (const { address } of SOLANA_STABLECOINS) add(CHAIN_IDS.SOLANA, address);
  return map;
})();

/**
 * Superstate Tokens
 */
export const EVM_SUPERSTATE_FUND_ALLOWLIST = '0x02f1fa8b196d21c7b733eb2700b825611d8a38e5' as const;
export const SVM_SUPERSTATE_TOKEN_ALLOWLIST =
  'HFkKyweJDUuGer5KaCst5qZSYD5aapKaD7xzdNaoRtfA' as const;

export const EVM_SUPERSTATE_FUND_TOKENS = [
  '0x14d60e7fdc0d71d8611742720e4c50e7a974020c', // USCC
  '0x43415eb6ff9db7e26a15b704e7a3edce97d31c4e', // USTB
];
export const SVM_SUPERSTATE_TOKENS = [
  '7GzQgf6DPo6ZANjnbhe9tNCpkGTv3zqHbsDx74jyQf9', // FORD
  '2HehXG149TXuVptQhbiWAWDjbbuCsXSAtLTB5wc2aajK', // GLXY
];

export const BRIDGED_USDC_PER_CHAIN_ID: {
  [chainId: number]: ChainAddress;
} = {
  [CHAIN_IDS.AVALANCHE]: '0xa7d7079b0fead91f3e65f86e8915cb59c1a4c664',
  [CHAIN_IDS.ARBITRUM]: '0xff970a61a04b1ca14834a43f5de4533ebddb5cc8',
  [CHAIN_IDS.BASE]: '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca',
  [CHAIN_IDS.MATIC]: '0x2791bca1f2de4661ed88a30c99a7a9449aa84174',
  [CHAIN_IDS.OPTIMISM]: '0x7f5c764cbc14f9669b88837ca1490cca17c31607',
};
